import { useCallback, useEffect, useMemo, useState } from 'react';

import {
  cancelIssueCoupon,
  cancelSpawnedCoupon,
  createAdditionalCoupons,
  CreateAdditionalCouponsDto,
  disposeAndReissueCoupons,
  disposeCoupons,
  ExtraPaymentDto,
  fulfillFromExisting,
  getDonorCandidates,
  issueCoupon,
  payExtraPayment,
  promoteToReissued,
  resetCouponToNotShared,
  updateCouponAdditionalRecipientPhone,
} from '@shared/api/giftCoupon/giftCoupon.controller';
import {
  GiftCouponRedeemStatus,
  GiftCouponShareStatus,
  GiftCouponSummary,
} from '@shared/api/giftCoupon/giftCoupon.interface';
import {
  cancelGiftOrder,
  partialCancelGiftOrder,
} from '@shared/api/giftOrder/giftOrder.controller';
import {
  GiftOrderItemType,
  GiftOrderPaymentStatus,
} from '@shared/api/giftOrder/giftOrder.interface';
import { toastError, toastSuccess } from '@shared/plugIn/toast';
import { LowerRole } from '@shared/types';

import { isCouponCancellable, isCouponDisposable, resolveGiftItemId } from './constants';
import { computeCrossCouponHashOverlap } from './kakaoShareOverlap';
import {
  CouponDetailModalProps,
  CouponDetailState,
  GiftItemEmbed,
  ReissueStatPerItem,
} from './types';

export function useCouponDetailState({
  coupons,
  orderId,
  order,
  onClose,
}: CouponDetailModalProps): CouponDetailState {
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [cancelReason, setCancelReason] = useState('');
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const [cancelResult, setCancelResult] = useState<string | null>(null);
  const [shareHistoryCoupon, setShareHistoryCoupon] =
    useState<GiftCouponSummary | null>(null);
  const [showAdditionalForm, setShowAdditionalForm] = useState(false);
  const [additionalCount, setAdditionalCount] = useState(1);
  const [isCreatingAdditional, setIsCreatingAdditional] = useState(false);
  const [additionalResult, setAdditionalResult] = useState<string | null>(null);
  const [reissueGiftItemId, setReissueGiftItemId] = useState('');
  // 한도 초과 강제 발행 모드 — 관리자 시크릿 키로 잠금 해제. 활성화 시 잔여 수량과
  // 무관하게 재발행을 허용한다. (admin 손실을 감수하는 예외 발행용)
  const [reissueOverride, setReissueOverride] = useState(false);

  // 답례 인사 일괄 발송 — 수신번호(additionalRecipientPhone) 있는 쿠폰을 선택해 LMS 발송
  const [courtesyMode, setCourtesyMode] = useState(false);
  const [courtesySelectedIds, setCourtesySelectedIds] = useState<Set<string>>(new Set());
  const [showCourtesyModal, setShowCourtesyModal] = useState(false);

  const [disposeMode, setDisposeMode] = useState(false);
  const [disposeAction, setDisposeAction] = useState<'dispose' | 'dispose-reissue'>(
    'dispose-reissue',
  );
  const [disposeSelectedIds, setDisposeSelectedIds] = useState<Set<string>>(new Set());
  const [showDisposeConfirm, setShowDisposeConfirm] = useState(false);
  const [disposeReason, setDisposeReason] = useState('');
  const [isDisposing, setIsDisposing] = useState(false);
  const [disposeResult, setDisposeResult] = useState<string | null>(null);
  const [isPromoting, setIsPromoting] = useState(false);
  const [previewCopied, setPreviewCopied] = useState(false);
  const [kakaoCallbackCoupon, setKakaoCallbackCoupon] =
    useState<GiftCouponSummary | null>(null);
  const [extraRedeemCoupon, setExtraRedeemCoupon] = useState<GiftCouponSummary | null>(
    null,
  );
  // 스폰(추가교환) 쿠폰 추가결제 대기건 처리 — 입금/대체발송 모달 대상 & 취소 진행상태
  const [spawnPayCoupon, setSpawnPayCoupon] = useState<GiftCouponSummary | null>(null);
  const [spawnFulfillCoupon, setSpawnFulfillCoupon] = useState<GiftCouponSummary | null>(
    null,
  );
  const [cancelingSpawnId, setCancelingSpawnId] = useState<string | null>(null);

  const sortedCoupons = useMemo(
    () => [...coupons].sort((a, b) => a.seq - b.seq),
    [coupons],
  );

  const crossCouponHashOverlap = useMemo(
    () => computeCrossCouponHashOverlap(coupons),
    [coupons],
  );

  const giftItemEmbedMap = useMemo(() => {
    const map = new Map<string, GiftItemEmbed>();
    if (!order?.orderDetails) return map;
    for (const d of order.orderDetails) {
      if (d.type !== GiftOrderItemType.Coupon) continue;
      const gid = resolveGiftItemId(d.giftItem);
      if (!gid) continue;
      if (!map.has(gid)) {
        map.set(gid, {
          name: d.giftItemEmbed.name,
          priceRetail: d.giftItemEmbed.priceRetail,
        });
      }
    }
    return map;
  }, [order]);

  const stats = useMemo(() => {
    const activeCoupons = coupons.filter(
      (c) => c.redeemStatus !== GiftCouponRedeemStatus.Disposed,
    );
    const originalCoupons = activeCoupons.filter((c) => !c.isAdditional);
    const additionalCoupons = activeCoupons.filter((c) => c.isAdditional);
    const disposedCount = coupons.filter(
      (c) => c.redeemStatus === GiftCouponRedeemStatus.Disposed,
    ).length;
    const reissuedCount = coupons.filter((c) => c.isReissued).length;
    const total = originalCoupons.length;
    const additionalTotal = additionalCoupons.length;
    const notShared = activeCoupons.filter(
      (c) => c.shareStatus === GiftCouponShareStatus.NotShared,
    ).length;
    const available = activeCoupons.filter(
      (c) =>
        c.redeemStatus !== GiftCouponRedeemStatus.Delivered &&
        c.redeemStatus !== GiftCouponRedeemStatus.Refunded &&
        c.redeemStatus !== GiftCouponRedeemStatus.Canceled,
    ).length;
    const delivered = activeCoupons.filter(
      (c) => c.redeemStatus === GiftCouponRedeemStatus.Delivered,
    ).length;
    return {
      total,
      additionalTotal,
      disposedCount,
      reissuedCount,
      notShared,
      available,
      delivered,
    };
  }, [coupons]);

  const reissueStatsByItem = useMemo<ReissueStatPerItem[]>(() => {
    const itemIds = Array.from(giftItemEmbedMap.keys());
    if (itemIds.length === 0) return [];

    return itemIds
      .map((gid) => {
        const embed = giftItemEmbedMap.get(gid);
        if (!embed) return null;
        const itemCoupons = coupons.filter((c) => c.giftItem === gid);
        const active = itemCoupons.filter(
          (c) => c.redeemStatus !== GiftCouponRedeemStatus.Disposed,
        );
        const originals = active.filter((c) => !c.isAdditional);
        const sharedNotViewed = originals.filter(
          (c) => c.shareStatus === GiftCouponShareStatus.Shared,
        ).length;
        const additionalIssued = active.filter((c) => c.isAdditional).length;
        return {
          giftItemId: gid,
          name: embed.name,
          priceRetail: embed.priceRetail,
          sharedNotViewed,
          additionalIssued,
          remaining: sharedNotViewed - additionalIssued,
        };
      })
      .filter((v): v is ReissueStatPerItem => v !== null);
  }, [coupons, giftItemEmbedMap]);

  const selectedReissueStat = useMemo(
    () => reissueStatsByItem.find((s) => s.giftItemId === reissueGiftItemId),
    [reissueStatsByItem, reissueGiftItemId],
  );

  const totalReissueRemaining = useMemo(
    () => reissueStatsByItem.reduce((sum, s) => sum + Math.max(0, s.remaining), 0),
    [reissueStatsByItem],
  );

  useEffect(() => {
    const first = reissueStatsByItem[0];
    if (first && !reissueStatsByItem.some((s) => s.giftItemId === reissueGiftItemId)) {
      setReissueGiftItemId(first.giftItemId);
    }
  }, [reissueStatsByItem, reissueGiftItemId]);

  useEffect(() => {
    if (!selectedReissueStat || reissueOverride) return;
    const max = Math.max(1, selectedReissueStat.remaining);
    setAdditionalCount((prev) => Math.min(Math.max(1, prev), max));
  }, [selectedReissueStat, reissueOverride]);

  const cancellableCoupons = useMemo(
    () => sortedCoupons.filter(isCouponCancellable),
    [sortedCoupons],
  );

  const canCancel = useMemo(() => {
    if (!orderId || !order) return false;
    const status = order.paymentDetail.status;
    return (
      status === GiftOrderPaymentStatus.Paid ||
      status === GiftOrderPaymentStatus.Confirmed
    );
  }, [orderId, order]);

  const hasDeliveryItems = useMemo(() => {
    if (!order) return false;
    return order.orderDetails.some((d) => d.type === GiftOrderItemType.Delivery);
  }, [order]);

  const allNotSharedDeliveries = useMemo(() => {
    if (!order || !order.deliveries) return true;
    const deliveries = order.deliveries as {
      shareStatus: string;
      deliveryStatus?: string;
    }[];
    return deliveries.every((d) => d.shareStatus === 'NotShared');
  }, [order]);

  const isFullCancel = useMemo(() => {
    if (!canCancel) return false;
    const activeCoupons = coupons.filter(
      (c) => c.redeemStatus !== GiftCouponRedeemStatus.Canceled,
    );
    const allCouponsSelected =
      activeCoupons.length > 0 && activeCoupons.every((c) => selectedIds.has(c._id));
    if (!allCouponsSelected) return false;
    if (hasDeliveryItems) return allNotSharedDeliveries;
    return true;
  }, [canCancel, coupons, selectedIds, hasDeliveryItems, allNotSharedDeliveries]);

  const disposableCoupons = useMemo(
    () => sortedCoupons.filter(isCouponDisposable),
    [sortedCoupons],
  );

  // 답례 인사 발송 대상 후보: 수신번호(확인번호) 있는 쿠폰.
  // [테스트] 취소건도 발송 가능하도록 Canceled는 제외하지 않는다. (폐기건만 제외)
  const courtesyEligibleCoupons = useMemo(
    () =>
      sortedCoupons.filter(
        (c) =>
          !!c.additionalRecipientPhone &&
          c.redeemStatus !== GiftCouponRedeemStatus.Disposed,
      ),
    [sortedCoupons],
  );

  // 답례 발송/폐기 모드는 상호 배타 — 한쪽을 켜면 다른 쪽의 선택을 정리한다.
  useEffect(() => {
    if (courtesyMode) {
      setDisposeMode(false);
      setDisposeSelectedIds(new Set());
    }
  }, [courtesyMode]);

  useEffect(() => {
    if (disposeMode) {
      setCourtesyMode(false);
      setCourtesySelectedIds(new Set());
    }
  }, [disposeMode]);

  // --- Callbacks ---

  const toggleSelect = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const openShareHistoryModal = useCallback((coupon: GiftCouponSummary) => {
    setShareHistoryCoupon(coupon);
  }, []);

  const closeShareHistoryModal = useCallback(() => {
    setShareHistoryCoupon(null);
  }, []);

  const toggleSelectAll = useCallback(() => {
    if (selectedIds.size === cancellableCoupons.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(cancellableCoupons.map((c) => c._id)));
    }
  }, [cancellableCoupons, selectedIds.size]);

  const handleCancelRequest = useCallback(async () => {
    if (!orderId || selectedIds.size === 0 || !cancelReason.trim()) return;

    setIsCancelling(true);
    setCancelResult(null);

    try {
      if (isFullCancel) {
        await cancelGiftOrder(
          { _id: orderId, cancelReason: cancelReason.trim() },
          LowerRole.Admin,
        );
        setCancelResult('전체취소 요청이 완료되었습니다.');
      } else {
        const result = await partialCancelGiftOrder({
          _id: orderId,
          cancelReason: cancelReason.trim(),
          couponIds: Array.from(selectedIds),
          deliveryIds: [],
        });
        setCancelResult(
          `부분취소 완료: ${result.data.cancelledPrice.toLocaleString()}원 환불`,
        );
      }
      setShowCancelConfirm(false);
      setSelectedIds(new Set());
      setCancelReason('');
      setTimeout(() => {
        onClose();
        window.location.reload();
      }, 1500);
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : '취소 처리 중 오류가 발생했습니다.';
      setCancelResult(`오류: ${message}`);
    } finally {
      setIsCancelling(false);
    }
  }, [orderId, selectedIds, cancelReason, isFullCancel, onClose]);

  const handleCreateAdditional = useCallback(async () => {
    if (!orderId || additionalCount < 1 || !reissueGiftItemId) return;
    const stat = reissueStatsByItem.find((s) => s.giftItemId === reissueGiftItemId);
    if (!stat) return;
    // 강제 발행 모드가 아니면 잔여 수량 한도를 검증한다.
    if (!reissueOverride && additionalCount > stat.remaining) {
      setAdditionalResult(
        `오류: 재발행 가능 수량을 초과합니다. (최대 ${stat.remaining}개)`,
      );
      return;
    }

    setIsCreatingAdditional(true);
    setAdditionalResult(null);

    try {
      const dto: CreateAdditionalCouponsDto = {
        giftOrderId: orderId,
        count: additionalCount,
        giftItemId: reissueGiftItemId,
      };
      const created = await createAdditionalCoupons(dto);
      setAdditionalResult(`재발행 완료: ${created.length}개 쿠폰이 생성되었습니다.`);
      setShowAdditionalForm(false);
      setAdditionalCount(1);
      setTimeout(() => {
        onClose();
        window.location.reload();
      }, 1500);
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : '재발행 처리 중 오류가 발생했습니다.';
      setAdditionalResult(`오류: ${message}`);
    } finally {
      setIsCreatingAdditional(false);
    }
  }, [
    orderId,
    additionalCount,
    reissueGiftItemId,
    reissueStatsByItem,
    reissueOverride,
    onClose,
  ]);

  // 한도 초과 강제 발행 잠금 해제 — 관리자 시크릿 키(미리보기 키)를 입력받아
  // 일치하면 잔여 수량과 무관하게 재발행할 수 있도록 override 모드를 켠다.
  const requestReissueOverride = useCallback(() => {
    if (reissueOverride) return;
    const expected = process.env.REACT_APP_GIFT_STORE_ADMIN_PREVIEW_KEY ?? '';
    const input = window.prompt(
      '재발행 한도를 무시하고 강제로 발행하려면 관리자 시크릿 키를 입력하세요.\n(admin 손실이 발생할 수 있는 예외 발행입니다.)',
    );
    if (input == null) return; // 취소
    if (!expected || input.trim() !== expected) {
      alert('시크릿 키가 일치하지 않습니다.');
      return;
    }
    setReissueOverride(true);
    setShowAdditionalForm(true);
  }, [reissueOverride]);

  const toggleDisposeSelect = useCallback((id: string) => {
    setDisposeSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const toggleDisposeSelectAll = useCallback(() => {
    if (disposeSelectedIds.size === disposableCoupons.length) {
      setDisposeSelectedIds(new Set());
    } else {
      setDisposeSelectedIds(new Set(disposableCoupons.map((c) => c._id)));
    }
  }, [disposableCoupons, disposeSelectedIds.size]);

  const toggleCourtesySelect = useCallback((id: string) => {
    setCourtesySelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const toggleCourtesySelectAll = useCallback(() => {
    setCourtesySelectedIds((prev) =>
      prev.size === courtesyEligibleCoupons.length
        ? new Set()
        : new Set(courtesyEligibleCoupons.map((c) => c._id)),
    );
  }, [courtesyEligibleCoupons]);

  const handleDispose = useCallback(async () => {
    if (disposeSelectedIds.size === 0) return;

    setIsDisposing(true);
    setDisposeResult(null);

    try {
      if (disposeAction === 'dispose') {
        const disposed = await disposeCoupons({
          couponIds: Array.from(disposeSelectedIds),
          reason: disposeReason.trim() || undefined,
        });
        setDisposeResult(`폐기 ${disposed.length}건 완료`);
      } else {
        const result = await disposeAndReissueCoupons({
          couponIds: Array.from(disposeSelectedIds),
          reason: disposeReason.trim() || undefined,
        });
        setDisposeResult(
          `폐기 ${result.disposed.length}건 + 재발급 ${result.reissued.length}건 완료`,
        );
      }
      setShowDisposeConfirm(false);
      setDisposeSelectedIds(new Set());
      setDisposeReason('');
      setDisposeMode(false);
      setTimeout(() => {
        onClose();
        window.location.reload();
      }, 1500);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : '처리 중 오류가 발생했습니다.';
      setDisposeResult(`오류: ${message}`);
    } finally {
      setIsDisposing(false);
    }
  }, [disposeSelectedIds, disposeReason, disposeAction, onClose]);

  const handlePromoteToReissued = useCallback(
    async (couponId: string) => {
      if (
        !confirm(
          '이 재발행 쿠폰을 재발급(정식 대체)으로 승격하시겠습니까?\nisAdditional → isReissued로 변경되며, 통계에서 일반 쿠폰으로 집계됩니다.',
        )
      )
        return;
      setIsPromoting(true);
      setDisposeResult(null);
      try {
        await promoteToReissued(couponId);
        setDisposeResult('승격 완료: 재발행 → 재발급으로 변경되었습니다.');
        setTimeout(() => {
          onClose();
          window.location.reload();
        }, 1500);
      } catch (err: unknown) {
        const message =
          err instanceof Error ? err.message : '승격 처리 중 오류가 발생했습니다.';
        setDisposeResult(`오류: ${message}`);
      } finally {
        setIsPromoting(false);
      }
    },
    [onClose],
  );

  const [isResettingShare, setIsResettingShare] = useState(false);

  // 공유상태 뱃지 클릭 → 미전달(NotShared) + 교환가능(Available) 으로 되돌리기.
  // 낙관 전달/오발송 등으로 잘못 Shared 처리된 쿠폰을 운영자가 수동 정정하는 용도.
  const handleResetShareStatus = useCallback(
    async (couponId: string) => {
      if (
        !confirm(
          '이 쿠폰을 미전달(NotShared) + 교환가능(Available) 상태로 변경하시겠습니까?',
        )
      )
        return;
      setIsResettingShare(true);
      try {
        await resetCouponToNotShared(couponId);
        onClose();
        window.location.reload();
      } catch (err: unknown) {
        const message =
          err instanceof Error ? err.message : '미전달 처리 중 오류가 발생했습니다.';
        alert(`오류: ${message}`);
        console.error(err);
        setIsResettingShare(false);
      }
    },
    [onClose],
  );

  // 신청자(확인) 휴대폰번호 인라인 저장.
  // 여러 행을 연속으로 수정할 수 있으므로 저장 후 새로고침하지 않고 토스트로만 알린다.
  // (행이 저장값을 낙관적으로 표시 유지) 빈 값은 번호 삭제로 처리한다.
  const handleUpdateAdditionalPhone = useCallback(
    async (couponId: string, newPhone: string): Promise<boolean> => {
      const cleaned = newPhone.replace(/[^0-9]/g, '');
      if (cleaned && (cleaned.length < 10 || cleaned.length > 11)) {
        toastError('올바른 전화번호를 입력해주세요.');
        return false;
      }
      try {
        await updateCouponAdditionalRecipientPhone(couponId, cleaned);
        toastSuccess(cleaned ? '신청자 번호가 저장되었습니다.' : '신청자 번호를 삭제했습니다.');
        return true;
      } catch (err: unknown) {
        toastError(err instanceof Error ? err.message : '번호 저장에 실패했습니다.');
        return false;
      }
    },
    [],
  );

  // --- Refund/Issue result ---
  const [refundResult, setRefundResult] = useState<string | null>(null);

  // --- Issue (환불/연장 링크 발행) ---

  // --- Issue (환불/연장 링크 발행) ---
  const [isIssuing, setIsIssuing] = useState(false);

  const handleIssueRefund = useCallback(
    async (couponId: string) => {
      if (
        !confirm(
          '환불 링크를 발행하시겠습니까?\n고객에게 환불 신청 페이지 링크가 생성됩니다.',
        )
      )
        return;
      setIsIssuing(true);
      setRefundResult(null);
      try {
        await issueCoupon({ couponId, type: 'refund' });
        const refundUrl = `${window.location.origin}/gift-store/coupon/${couponId}/refund`;
        await navigator.clipboard.writeText(refundUrl);
        setRefundResult(`환불 링크가 발행되고 클립보드에 복사되었습니다.`);
        setTimeout(() => {
          onClose();
          window.location.reload();
        }, 2000);
      } catch (err: unknown) {
        const message =
          err instanceof Error ? err.message : 'Issue 발행 중 오류가 발생했습니다.';
        setRefundResult(`오류: ${message}`);
      } finally {
        setIsIssuing(false);
      }
    },
    [onClose],
  );

  const handleIssueRenewal = useCallback(
    async (couponId: string) => {
      if (
        !confirm(
          '연장 링크를 발행하시겠습니까?\n고객에게 연장 신청 페이지 링크가 생성됩니다.',
        )
      )
        return;
      setIsIssuing(true);
      setRefundResult(null);
      try {
        await issueCoupon({ couponId, type: 'renewal' });
        const renewalUrl = `${window.location.origin}/gift-store/coupon/${couponId}/renewal`;
        await navigator.clipboard.writeText(renewalUrl);
        setRefundResult(`연장 링크가 발행되고 클립보드에 복사되었습니다.`);
        setTimeout(() => {
          onClose();
          window.location.reload();
        }, 2000);
      } catch (err: unknown) {
        const message =
          err instanceof Error ? err.message : '연장 링크 발행 중 오류가 발생했습니다.';
        setRefundResult(`오류: ${message}`);
      } finally {
        setIsIssuing(false);
      }
    },
    [onClose],
  );

  const openKakaoCallbackModal = useCallback((coupon: GiftCouponSummary) => {
    setKakaoCallbackCoupon(coupon);
  }, []);

  const closeKakaoCallbackModal = useCallback(() => {
    setKakaoCallbackCoupon(null);
  }, []);

  const openExtraRedeemModal = useCallback((coupon: GiftCouponSummary) => {
    setExtraRedeemCoupon(coupon);
  }, []);

  const closeExtraRedeemModal = useCallback(() => {
    setExtraRedeemCoupon(null);
  }, []);

  // --- 스폰(추가교환) 쿠폰 추가결제 대기건 처리 (입금 / 대체발송 / 취소) ---

  const openSpawnPayModal = useCallback((coupon: GiftCouponSummary) => {
    setSpawnPayCoupon(coupon);
  }, []);
  const closeSpawnPayModal = useCallback(() => {
    setSpawnPayCoupon(null);
  }, []);
  const openSpawnFulfillModal = useCallback((coupon: GiftCouponSummary) => {
    setSpawnFulfillCoupon(coupon);
  }, []);
  const closeSpawnFulfillModal = useCallback(() => {
    setSpawnFulfillCoupon(null);
  }, []);

  // (A) 입금(추가결제) 후 발송 — 성공 시 재조회 위해 reload
  const handleSpawnPay = useCallback(
    async (couponId: string, dto: ExtraPaymentDto) => {
      try {
        await payExtraPayment(couponId, dto);
        setTimeout(() => {
          onClose();
          window.location.reload();
        }, 800);
        return true;
      } catch (err: unknown) {
        alert(err instanceof Error ? err.message : '추가결제 처리에 실패했습니다.');
        return false;
      }
    },
    [onClose],
  );

  // (B) 기구매 미전달 쿠폰으로 대체발송
  const handleSpawnFulfill = useCallback(
    async (couponId: string, donorCouponId: string) => {
      try {
        await fulfillFromExisting(couponId, donorCouponId);
        setTimeout(() => {
          onClose();
          window.location.reload();
        }, 800);
        return true;
      } catch (err: unknown) {
        alert(err instanceof Error ? err.message : '대체발송 처리에 실패했습니다.');
        return false;
      }
    },
    [onClose],
  );

  // (C) 신청 취소 (스폰 쿠폰 hard-delete + 부수효과 되돌리기)
  const handleCancelSpawn = useCallback(
    async (couponId: string) => {
      if (
        !confirm(
          '이 추가교환 신청을 취소하시겠습니까?\n신청 쿠폰이 삭제되고 원본 쿠폰의 추가교환 허용 슬롯이 복구됩니다.\n(실제 결제된 건은 취소할 수 없으며 환불로 처리해야 합니다.)',
        )
      )
        return;
      setCancelingSpawnId(couponId);
      try {
        await cancelSpawnedCoupon(couponId);
        onClose();
        window.location.reload();
      } catch (err: unknown) {
        alert(err instanceof Error ? err.message : '신청 취소에 실패했습니다.');
        setCancelingSpawnId(null);
      }
    },
    [onClose],
  );

  const handleCancelIssue = useCallback(
    async (couponId: string) => {
      if (!confirm('발행된 링크를 취소하시겠습니까?')) return;
      setIsIssuing(true);
      setRefundResult(null);
      try {
        await cancelIssueCoupon(couponId);
        setRefundResult('링크 발행이 취소되었습니다.');
        setTimeout(() => {
          onClose();
          window.location.reload();
        }, 1500);
      } catch (err: unknown) {
        const message =
          err instanceof Error ? err.message : 'Issue 취소 중 오류가 발생했습니다.';
        setRefundResult(`오류: ${message}`);
      } finally {
        setIsIssuing(false);
      }
    },
    [onClose],
  );

  return {
    sortedCoupons,
    giftItemEmbedMap,
    stats,
    reissueStatsByItem,
    selectedReissueStat,
    totalReissueRemaining,
    cancellableCoupons,
    disposableCoupons,
    canCancel,
    hasDeliveryItems,
    isFullCancel,

    selectedIds,
    cancelReason,
    setCancelReason,
    showCancelConfirm,
    setShowCancelConfirm,
    isCancelling,
    cancelResult,
    toggleSelect,
    toggleSelectAll,
    handleCancelRequest,

    showAdditionalForm,
    setShowAdditionalForm,
    additionalCount,
    setAdditionalCount,
    isCreatingAdditional,
    additionalResult,
    reissueGiftItemId,
    setReissueGiftItemId,
    handleCreateAdditional,
    reissueOverride,
    requestReissueOverride,

    disposeMode,
    setDisposeMode,
    disposeAction,
    setDisposeAction,
    disposeSelectedIds,
    setDisposeSelectedIds,
    showDisposeConfirm,
    setShowDisposeConfirm,
    disposeReason,
    setDisposeReason,
    isDisposing,
    disposeResult,
    toggleDisposeSelect,
    toggleDisposeSelectAll,
    handleDispose,

    isPromoting,
    handlePromoteToReissued,

    // 답례 인사 일괄 발송
    courtesyMode,
    setCourtesyMode,
    courtesySelectedIds,
    setCourtesySelectedIds,
    courtesyEligibleCoupons,
    toggleCourtesySelect,
    toggleCourtesySelectAll,
    showCourtesyModal,
    setShowCourtesyModal,

    isResettingShare,
    handleResetShareStatus,

    handleUpdateAdditionalPhone,

    // refund/issue result
    refundResult,

    // issue
    isIssuing,
    handleIssueRefund,
    handleIssueRenewal,
    handleCancelIssue,

    shareHistoryCoupon,
    openShareHistoryModal,
    closeShareHistoryModal,

    kakaoCallbackCoupon,
    openKakaoCallbackModal,
    closeKakaoCallbackModal,

    extraRedeemCoupon,
    openExtraRedeemModal,
    closeExtraRedeemModal,

    // 스폰(추가교환) 추가결제 대기건 처리
    spawnPayCoupon,
    spawnFulfillCoupon,
    cancelingSpawnId,
    openSpawnPayModal,
    closeSpawnPayModal,
    openSpawnFulfillModal,
    closeSpawnFulfillModal,
    handleSpawnPay,
    handleSpawnFulfill,
    handleCancelSpawn,
    getDonorCandidates,

    crossCouponHashOverlap,

    previewCopied,
    setPreviewCopied,
  };
}
